import React from 'react';
import { useTranslation } from 'react-i18next';
import Picture from './Picture';
import FilmPlayer from './FilmPlayer';
import { toMediaLang } from './media';
import { CONTACT_EMAIL, useDocumentTitle } from './links';
import { useEditionKey, useSite } from './edition';

interface Founder {
  name: string;
  role: string;
  body: string;
}

/** Unternehmen / Company (concept §5). */
const CompanyV2Page: React.FC = () => {
  const { t, i18n } = useTranslation();
  const lang = toMediaLang(i18n.language);
  const { edition } = useSite();
  const ek = useEditionKey();
  useDocumentTitle(t('homeV2.company.meta.title'));

  const founders = t(ek('company.founders'), { returnObjects: true }) as Founder[];
  const recognition = t('homeV2.company.recognition', { returnObjects: true }) as string[];

  return (
    <div className="hv2-page">
      <header className="hv2-page__header">
        <h1>{t('homeV2.company.title')}</h1>
        <p className="hv2-page__lead">{t('homeV2.company.lead')}</p>
        <p>{t('homeV2.company.why')}</p>
      </header>

      <section className="hv2-page__section hv2-founders">
        <h2>{t(ek('company.foundersTitle'))}</h2>
        {/* The founders' portrait shows Antonio, so only the founders edition carries it (HOF-662). */}
        <div className={`hv2-founders__grid${edition === 'founders' ? '' : ' hv2-founders__grid--text-only'}`}>
          {edition === 'founders' && (
            <div className="hv2-founders__photo">
              <Picture
                name="team-founders-alt"
                widths={[533, 1066]}
                alt={t('homeV2.company.foundersPhotoAlt')}
                width={1066}
                height={1600}
                sizes="(max-width: 860px) 70vw, 360px"
              />
            </div>
          )}
          <div>
            {founders.map((f) => (
              <div className="hv2-person" key={f.name}>
                <p className="hv2-person__name">{f.name}</p>
                <p className="hv2-person__role">{f.role}</p>
                <p>{f.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="hv2-page__section">
        <h2>{t('homeV2.company.workTitle')}</h2>
        <p>{t(ek('company.work'))}</p>
      </section>

      <section className="hv2-page__section" id="pitch">
        <h2>{t('homeV2.company.recognitionTitle')}</h2>
        <ul className="hv2-list">
          {recognition.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
        <h3>{t('homeV2.company.pitchTitle')}</h3>
        {/* The 88 s event cut: German subtitles are burned in, so the German
            page carries no track and the English page turns English on. */}
        <FilmPlayer
          key={lang}
          film="pitch-de"
          poster="pitch-de-poster"
          captions={[{ src: 'pitch-en.vtt', srclang: 'en', label: 'English', default: lang === 'en' }]}
          title={t('homeV2.company.pitchPlay')}
          lang={lang}
        />
        <p className="hv2-note">{t('homeV2.company.pitchNote')}</p>
        {/* Event photo: publication rights open as concept D3. */}
        <div className="hv2-photos">
          <figure>
            <Picture name="company-neuss-pitch" widths={[960, 1920]} alt={t('homeV2.company.photos.neussPitch')} width={1920} height={1280} sizes="(max-width: 860px) 100vw, 560px" />
            <figcaption>{t('homeV2.company.photos.neussPitch')}</figcaption>
          </figure>
        </div>
      </section>

      <section className="hv2-page__section">
        <h2>{t('homeV2.company.statusTitle')}</h2>
        <p>{t('homeV2.company.status')}</p>
      </section>

      <section className="hv2-page__section">
        <h2>{t('homeV2.company.investorsTitle')}</h2>
        <p>{t('homeV2.company.investors')}</p>
        <a className="hv2-link" href={`mailto:${CONTACT_EMAIL}`}>
          {t('homeV2.company.investorsCta')}: {CONTACT_EMAIL}
        </a>
      </section>
    </div>
  );
};

export default CompanyV2Page;
